import { MatterType } from './matterService'
import { MatterDocument } from './documentService'
import { API_BASE_URL } from '../config'

// Public: the client never logs in. Unlike uploadLinkService.ts, the
// unguessable token in the URL is NOT sufficient on its own — every call
// also carries the client's email + this matter's settlement date as a
// second factor (see verifyPortalCredentials in backend/src/index.ts),
// since this link may travel by email rather than being handed over in
// person. POST throughout (even for the initial "load" call) so those
// credentials travel in the body, not a logged query string.

export type IdentityCheckMethod = 'manual' | 'didit'
export type IdentityCheckStatus = 'pending' | 'verified' | 'rejected'

// A universal 4-phase progress tracker (see backend/src/task-phases.ts) —
// every matter type's real, matter-type-specific task list is bucketed into
// these same four phases server-side, so the client always sees the same
// four labels regardless of Sale/Purchase/Transfer/Subdivision.
export type TaskPhaseId = 'getting-started' | 'searches-preparation' | 'final-checks' | 'settlement'

export interface PortalPhase {
  id: TaskPhaseId
  label: string
  status: 'completed' | 'current' | 'upcoming'
  dueDate: string | null
}

export interface PortalIdentityCheck {
  id: string
  method: IdentityCheckMethod
  docType: string
  status: IdentityCheckStatus
  createdAt: string
  // Only present while status is 'pending', so the client can resume/finish
  // a Didit session that hasn't posted a result yet.
  verificationUrl: string | null
  // Server-computed (see backend/src/identity-check-points.ts's
  // pointsForCheck) — null unless status is 'verified'. Shown to the client
  // so their view matches what staff see in IdentityVerificationPanel.tsx.
  points: number | null
}

// Same shape as identityCheckService.ts's IdentityCheckPointsSummary — kept
// as a separate type here since this service has no auth dependency on that
// one, but the fields must stay identical (both come from
// backend/src/identity-check-points.ts's summarizeIdentityCheckPoints).
export interface PortalIdentityCheckPointsSummary {
  totalPoints: number
  required: number
  hasPrimary: boolean
  met: boolean
}

export interface Portal {
  clientName: string
  matterType: MatterType
  propertyAddress: string
  settlementDate: string | null
  phases: PortalPhase[]
  identityChecks: PortalIdentityCheck[]
  // The same 100-point summary the tenant panel shows, so both sides agree
  // on progress/expectations.
  pointsSummary: PortalIdentityCheckPointsSummary
  documents: MatterDocument[]
}

export interface PortalCredentials {
  email: string
  settlementDate: string
}

export const loadPortal = async (token: string, credentials: PortalCredentials): Promise<Portal> => {
  const response = await fetch(`${API_BASE_URL}/client-portal-links/${token}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  })
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data?.error || `This link is not valid (${response.status})`)
  }

  return data
}

const uploadFileToS3 = async (uploadUrl: string, file: File): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`)
  }
}

export const uploadPortalDocument = async (
  token: string,
  credentials: PortalCredentials,
  file: File,
): Promise<MatterDocument> => {
  const response = await fetch(`${API_BASE_URL}/client-portal-links/${token}/documents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...credentials, fileName: file.name, contentType: file.type || 'application/octet-stream' }),
  })
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data?.error || `Failed to start upload (${response.status})`)
  }

  await uploadFileToS3(data.uploadUrl, file)

  return data.document
}

export const startPortalIdentityCheck = async (
  token: string,
  credentials: PortalCredentials,
): Promise<{ identityCheck: PortalIdentityCheck; verificationUrl: string | null }> => {
  const response = await fetch(`${API_BASE_URL}/client-portal-links/${token}/identity-checks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // appBaseUrl tells the backend where this frontend is actually running,
    // so the Didit callback redirect lands here (e.g. localhost in dev)
    // rather than a deploy-time-only backend URL — see resolveAppBaseUrl in
    // backend/src/index.ts.
    body: JSON.stringify({ ...credentials, appBaseUrl: window.location.origin }),
  })
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data?.error || `Failed to start verification (${response.status})`)
  }

  return data
}

// Submits one supporting ID document (already uploaded via
// uploadPortalDocument) toward the 100-point identity check — see
// backend/src/identity-check-points.ts. Lands as 'pending' for staff review,
// same as the legacy tenant-uploaded manual path — except for docType
// 'medicare-card', where medicareCardNumber/dateOfBirth let the backend run
// Didit's Australia Medicare database validation synchronously (see
// buildMedicareCheckFields in backend/src/index.ts), so the check can come
// back already verified/rejected instead of always landing 'pending'.
export const submitPortalManualIdentityCheck = async (
  token: string,
  credentials: PortalCredentials,
  input: { docType: string; documentId: string; medicareCardNumber?: string; dateOfBirth?: string },
): Promise<PortalIdentityCheck> => {
  const response = await fetch(`${API_BASE_URL}/client-portal-links/${token}/identity-checks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...credentials, method: 'manual', ...input }),
  })
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data?.error || `Failed to submit document (${response.status})`)
  }

  return data.identityCheck
}
